import PropTypes from 'prop-types';
import React from 'react';
import Box from '../box/box.jsx';
import styles from './extension-preview.css';
import * as scratchblocks from '@turbowarp/scratchblocks';

// Convert one TurboWarp/Scratch extension block definition into scratchblocks
// syntax so it can be rendered as a real Scratch block. This mirrors the
// ow-extension website's blockToScratchblocks (development/homepage-template.ejs)
// exactly, including the same skip rules, so the desktop preview matches the
// website: palette labels/menus/separators and blocks without text are skipped,
// and [NAME] placeholders become string inputs while [NAME v] becomes a dropdown.
// Returns null for blocks that should not be rendered.
const DEFAULT_BLOCK_COLOR = '#4c97ff';
// VM default (runtime defaultExtensionColors[0]). Extensions that do not declare
// their own color1 use this; the editor shows those blocks in the platform's
// default extension green, so the preview uses that same green below.
const VM_DEFAULT_EXTENSION_COLOR = '#0FBD8C';
// Website default extension green.
const DEFAULT_EXTENSION_COLOR = '#6ed561';
const blockToScratchblocks = (block, color) => {
    const type = block.blockType || 'command';
    // Palette labels, menus, and separators are not real blocks.
    if (type === 'label' || type === 'menu' || type === 'separator') {
        return null;
    }
    let shape;
    if (type === 'hat') shape = 'hat';
    else if (type === 'reporter') shape = 'reporter';
    else if (type === 'boolean') shape = 'boolean';
    else shape = 'stack';

    let text = block.text;
    if (typeof text === 'function') {
        try { text = text(); } catch (e) { text = ''; }
    }
    text = String(text || '');
    // Skip blocks with no usable label (e.g. button blocks), otherwise they
    // render as blank blocks.
    if (!text) {
        return null;
    }

    // [NAME] placeholders become string inputs; [NAME v] becomes a dropdown.
    const regex = /\[([^\]]+)\]/g;
    let out = '';
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
        out += text.slice(lastIndex, match.index);
        const token = match[1];
        const arg = block.arguments && block.arguments[token];
        out += (arg && arg.menu) ? `[${token} v]` : `[${token}]`;
        lastIndex = regex.lastIndex;
    }
    out += text.slice(lastIndex);

    const overrides = [];
    if (shape !== 'stack') overrides.push(shape);
    overrides.push(color || DEFAULT_BLOCK_COLOR);
    return `${out} :: ${overrides.join(' :: ')}`;
};

// The preview uses the VM as its only source of block metadata. The extension is
// loaded into the real VM (the same environment it runs in when the user adds it)
// and we render the getInfo() the VM registered — this is exactly the source the
// "已加载扩展" page uses, so both previews look identical, including block colours.
// If the preview had to load the extension itself, it is unloaded again when the
// preview closes so merely previewing does not permanently add it to the project.
const unloadExtension = (vm, extensionId) => {
    if (!vm || !extensionId) return;
    const em = vm.extensionManager;
    const runtime = vm.runtime;

    let serviceName = null;
    if (em && em._loadedExtensions) {
        serviceName = em._loadedExtensions.get(extensionId);
        em._loadedExtensions.delete(extensionId);
    }
    if (em && em._loadedExtensionInfos) {
        em._loadedExtensionInfos.delete(extensionId);
    }
    if (runtime && `ext_${extensionId}` in runtime) {
        delete runtime[`ext_${extensionId}`];
    }
    if (runtime && Array.isArray(runtime._blockInfo)) {
        runtime._blockInfo = runtime._blockInfo.filter(category => category.id !== extensionId);
    }
    if (em && serviceName) {
        const workerId = +String(serviceName).split('.')[1];
        if (Number.isFinite(workerId)) {
            if (em.workerURLs) delete em.workerURLs[workerId];
            if (em.pendingWorkers) delete em.pendingWorkers[workerId];
        }
    }
    if (typeof vm.emitWorkspaceUpdate === 'function') {
        vm.emitWorkspaceUpdate();
    }
};

class ExtensionPreviewComponent extends React.PureComponent {
    constructor (props) {
        super(props);
        this.blocksContainerRef = React.createRef();
        this.didLoadForPreview = false;
        this.state = {
            status: 'loading',
            info: null,
            error: null
        };
    }
    componentDidMount () {
        this.load();
    }
    componentDidUpdate (prevProps, prevState) {
        if (prevProps.extensionURL !== this.props.extensionURL ||
            prevProps.extensionId !== this.props.extensionId) {
            this.load();
        }
        if (this.state.status === 'loaded' && this.state.info &&
            (prevState.status !== 'loaded' || prevState.info !== this.state.info)) {
            this.renderScratchblocks();
        }
    }
    componentWillUnmount () {
        this.didUnmount = true;
        if (this.didLoadForPreview) {
            this.didLoadForPreview = false;
            unloadExtension(this.props.vm, this.getLoadedExtensionId());
        }
    }
    /**
     * The id under which this extension is currently loaded in the VM, matching
     * by id or (for URL extensions whose internal id we may not know up-front)
     * by the extension URL.
     * @returns {string|null}
     */
    getLoadedExtensionId () {
        const {extensionId, extensionURL, vm} = this.props;
        const em = vm && vm.extensionManager;
        if (!em) return null;
        if (extensionId && typeof em.isExtensionLoaded === 'function' && em.isExtensionLoaded(extensionId)) {
            return extensionId;
        }
        if (extensionURL && typeof em.getExtensionURLs === 'function') {
            const urls = em.getExtensionURLs();
            for (const id of Object.keys(urls)) {
                if (urls[id] === extensionURL) {
                    return id;
                }
            }
        }
        return null;
    }
    getVmExtensionInfo () {
        const {extensionId, vm} = this.props;
        const em = vm && vm.extensionManager;
        if (!em || typeof em.getLoadedExtensionInfo !== 'function') {
            return null;
        }
        const loadedId = this.getLoadedExtensionId();
        if (loadedId) {
            return em.getLoadedExtensionInfo(loadedId) || null;
        }
        if (extensionId) {
            return em.getLoadedExtensionInfo(extensionId) || null;
        }
        return null;
    }
    load () {
        this.setState({status: 'loading', info: null, error: null});

        // Already loaded in the VM: read the real, VM-sanitised getInfo().
        const info = this.getVmExtensionInfo();
        if (info) {
            this.setState({status: 'loaded', info});
            return;
        }

        // Not loaded yet: load it into the VM (the real sandbox) and read the
        // getInfo() it registers. Unloaded again on close.
        const {extensionURL, extensionId, vm} = this.props;
        const em = vm && vm.extensionManager;
        const target = extensionURL || extensionId;
        if (em && target && typeof em.loadExtensionURL === 'function') {
            this.didLoadForPreview = true;
            Promise.resolve()
                .then(() => em.loadExtensionURL(target))
                .then(() => {
                    if (this.didUnmount) return;
                    const loaded = this.getVmExtensionInfo();
                    if (loaded) {
                        this.setState({status: 'loaded', info: loaded});
                    } else {
                        this.setState({status: 'error', error: '扩展已加载，但未获取到积木信息。'});
                    }
                })
                .catch(err => {
                    if (this.didUnmount) return;
                    this.setState({status: 'error', error: err.message || String(err)});
                });
            return;
        }

        this.setState({
            status: 'error',
            error: '该扩展尚未加载，且无法自动加载。'
        });
    }
    /**
     * The colour the blocks palette actually uses for this extension. The VM
     * fills in a default color1 in runtime._blockInfo when the extension does not
     * declare one, whereas the manager's info.color1 is often undefined — using
     * that would paint every such extension the same default blue.
     * @returns {string|null}
     */
    getBlockColor () {
        const {extensionId, vm} = this.props;
        const loadedId = this.getLoadedExtensionId() || extensionId;
        const runtime = vm && vm.runtime;
        let categoryColor = null;
        if (loadedId && runtime && Array.isArray(runtime._blockInfo)) {
            const category = runtime._blockInfo.find(info => info.id === loadedId);
            if (category && category.color1) {
                categoryColor = category.color1;
            }
        }
        // Blocks that do not declare their own colour use the platform's default
        // extension green (never the custom-accent colour, which may be red).
        const defaultColor = DEFAULT_EXTENSION_COLOR;
        if (categoryColor) {
            // Blocks without their own colour carry the VM default, so show the
            // platform green instead of the raw VM default.
            if (categoryColor.toLowerCase() === VM_DEFAULT_EXTENSION_COLOR.toLowerCase()) {
                return defaultColor;
            }
            return categoryColor;
        }
        const infoColor = this.state.info && this.state.info.color1;
        if (infoColor) {
            return infoColor;
        }
        return defaultColor;
    }
    renderScratchblocks () {
        const container = this.blocksContainerRef.current;
        if (!container) return;
        const color = this.getBlockColor();
        const blocks = (this.state.info && this.state.info.blocks) || [];
        container.innerHTML = '';
        blocks.forEach(block => {
            const line = blockToScratchblocks(block, color);
            if (!line) return;
            const pre = document.createElement('pre');
            pre.className = styles.scratchblocksBlock;
            pre.title = block.opcode || '';
            pre.textContent = line;
            container.appendChild(pre);
        });
        const sb = (scratchblocks && scratchblocks.default) ? scratchblocks.default : scratchblocks;
        if (sb && typeof sb.renderMatching === 'function') {
            // Render on the next frame so the (now visible) container has layout.
            requestAnimationFrame(() => {
                try {
                    sb.renderMatching('.' + styles.scratchblocksBlock, {style: 'scratch3'});
                } catch (e) {
                    // ignore rendering errors for individual extensions
                }
            });
        }
    }
    render () {
        const {status, info, error} = this.state;
        return (
            <Box
                className={styles.previewBody}
                grow={1}
            >
                {status === 'loading' && (
                    <div className={styles.status}>加载扩展中…</div>
                )}
                {status === 'error' && (
                    <div className={styles.status}>
                        {`无法预览积木：${error}`}
                    </div>
                )}
                {status === 'loaded' && info && (
                    <React.Fragment>
                        <div className={styles.previewHeader}>
                            <span className={styles.previewName}>
                                {info.name || info.id || '扩展'}
                            </span>
                            {info.version ? (
                                <span className={styles.previewVersion}>
                                    {`v${info.version}`}
                                </span>
                            ) : null}
                        </div>
                        {(info.blocks && info.blocks.length > 0) ? (
                            <div
                                className={styles.blocksContainer}
                                ref={this.blocksContainerRef}
                            />
                        ) : (
                            <div className={styles.status}>该扩展没有积木。</div>
                        )}
                    </React.Fragment>
                )}
            </Box>
        );
    }
}

ExtensionPreviewComponent.propTypes = {
    extensionURL: PropTypes.string,
    extensionId: PropTypes.string,
    vm: PropTypes.object,
    onClose: PropTypes.func
};

export default ExtensionPreviewComponent;
